import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
  useEffect,
  ComponentType,
  JSX,
  CSSProperties,
} from 'react';

import Editor from '@draft-js-plugins/editor';
import createMentionPlugin from '@draft-js-plugins/mention';
import type { Placement } from '@popperjs/core';
import {
  EditorState,
  getDefaultKeyBinding,
  EditorProps,
  convertFromRaw,
  RawDraftContentState,
} from 'draft-js';
import styled from 'styled-components';

import * as analytics from '@adsk/acs-web-analytics';
import { MenuCategory } from '@adsk/alloy-react-menu';
import Overlay from '@adsk/alloy-react-overlay';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme, { StylableComponent } from '@adsk/alloy-react-theme';

import { LoadingSuggestion } from './LoadingSuggestion';
import MentionCallout, { CalloutMentionProps } from './MentionCallout';
import MentionPopover, { MentionPopoverProps } from './MentionPopover';
import MentionSuggestion, { MentionSuggestionProps } from './MentionSuggestion';
import {
  defaultGetMentionName,
  defaultRegExp,
  MentionData,
  MENTION_ANALYTICS,
  MENTION_TYPES,
} from '../consts';
import { useTranslation } from '../i18n';
import { canShowCategory } from '../utils/canShowCategory';
import { convertToRawWithMentions } from '../utils/convertToRawWithMentions';
import draftJsCss from '../utils/draftJsCss';
import { isFirstRecentsSuggestion } from '../utils/isFirstRecentsSuggestion';
import { toCleanMention } from '../utils/toCleanMention';

const StyledDiv = styled.div`
  ${draftJsCss}
`;

function isFirst(suggestions: MentionData[], mention: MentionData) {
  const [first] = suggestions;
  return mention.id === first.id;
}

export type MentionPluginProps = Pick<
  StylableComponent<HTMLDivElement>,
  'style' | 'className'
> &
  Pick<
    EditorProps,
    | 'editorKey'
    | 'placeholder'
    | 'spellCheck'
    | 'onFocus'
    | 'onBlur'
    | 'readOnly'
  > & {
    autoFocus?: boolean;
    isLoading?: boolean;
    placement?: Placement;
    editorState?: EditorState;
    suggestions: MentionData[];
    currentUserId?: string | null;
    onChange?: (changedText: string, editorState: EditorState) => void;
    onSearchChange: (event: { trigger: string; value: string }) => void;
    onAddMention?(Mention: MentionData): void;
    mentionFormatter?: (mention: MentionData) => string;
    getMentionName?: (mention: MentionData) => string;
    getMentionType?: (mention: MentionData) => string;
    getMentionId?: (mention: MentionData) => string;
    renderPopover?: ComponentType<MentionPopoverProps>;
    renderSuggestion?: (props: MentionSuggestionProps) => JSX.Element;
    renderCallout?: (props: CalloutMentionProps) => JSX.Element;
  };

const categoryStyle = {
  h4: {
    background: theme.colors.charcoal050,
    height: 32,
    width: '100%',
    padding: '8px 16px',
    margin: 0,
  },
};

const MentionPlugin = ({
  className,
  style,
  editorKey,
  placeholder,
  readOnly,
  spellCheck,
  autoFocus,
  currentUserId,
  placement = Overlay.PLACEMENTS.BOTTOM_START,
  editorState: editorStateProp,
  suggestions,
  isLoading,
  onChange: onChangeProp,
  onAddMention,
  onSearchChange,
  onFocus,
  onBlur,
  getMentionName = defaultGetMentionName,
  getMentionType = (mention) => mention.type,
  getMentionId = (mention) => mention.id?.toString() || '',
  mentionFormatter = (mention) =>
    `@{"type": "${getMentionType(mention)}", "id": "${getMentionId(
      mention,
    )}", "name": "${getMentionName(mention)}"}`,
  // eslint-disable-next-line @adsk/react-render-props/component-to-render-prop
  renderPopover = MentionPopover,
  renderSuggestion = (props) => <MentionSuggestion {...props} />,
  renderCallout,
}: MentionPluginProps) => {
  const { t } = useTranslation();
  const editorRef = useRef<Editor>(null);
  const [editorState, setEditorState] = useState<EditorState>(
    EditorState.createEmpty(),
  );
  const [open, setOpen] = useState(false);

  const { nonRecentsSuggestions, recentsCount } = useMemo(() => {
    const nonRecents: MentionData[] = [];
    let count = 0;
    for (const s of suggestions) {
      if (s.fromRecents) count++;
      else nonRecents.push(s);
    }
    return { nonRecentsSuggestions: nonRecents, recentsCount: count };
  }, [suggestions]);

  const { MentionSuggestions, plugins } = useMemo(() => {
    const resolvedRenderCallout =
      renderCallout || ((props) => <MentionCallout {...props} />);
    const mentionPlugin = createMentionPlugin({
      supportWhitespace: true,
      entityMutability: 'IMMUTABLE',
      mentionComponent: ({ mention, ...props }) =>
        resolvedRenderCallout({
          mention: toCleanMention(mention as MentionData),
          ...props,
        }),
      popperOptions: {
        placement,
      },
      theme: {
        mentionSuggestionsPopup: 'mention-suggestions-popup',
      },
      mentionRegExp: defaultRegExp,
    });
    return {
      plugins: [mentionPlugin],
      MentionSuggestions: mentionPlugin.MentionSuggestions,
    };
  }, [placement, renderCallout]);

  const onOpenChange = useCallback((_open: boolean) => {
    setOpen(_open);

    if (_open) {
      analytics.track(MENTION_ANALYTICS.CREATE_START);
    }
  }, []);

  const onChange = useCallback(
    (nextEditorState: EditorState) => {
      onChangeProp?.(
        convertToRawWithMentions(nextEditorState, mentionFormatter),
        nextEditorState,
      );
      setEditorState(nextEditorState);
    },
    [mentionFormatter, onChangeProp],
  );

  useEffect(() => {
    if (autoFocus) {
      setImmediate(() => {
        editorRef?.current?.focus();
      });
    }
  }, [autoFocus]);

  return (
    <StyledDiv
      className={className}
      style={style as CSSProperties}
      onClick={() => editorRef.current?.focus()}
    >
      <Editor
        ref={editorRef}
        editorKey={editorKey}
        editorState={editorStateProp || editorState}
        onChange={onChange}
        plugins={plugins}
        onFocus={onFocus}
        placeholder={placeholder}
        spellCheck={spellCheck}
        readOnly={readOnly}
        onBlur={onBlur}
        keyBindingFn={(e: KeyboardEvent<HTMLTextAreaElement>) => {
          // Preserve default behavior of Mention plugin key bindings
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            return undefined;
          }

          if (e.key === 'Escape' && open) {
            analytics.track(MENTION_ANALYTICS.CREATE_END);
            return undefined;
          }

          return getDefaultKeyBinding(e);
        }}
      />
      <MentionSuggestions
        open={open}
        onOpenChange={onOpenChange}
        suggestions={suggestions}
        onSearchChange={onSearchChange}
        onAddMention={(mention) => {
          const raw = mention as MentionData;
          const selectedFromRecents = raw.fromRecents === true;
          const clean = toCleanMention(raw);
          onAddMention?.(clean);

          analytics.track(MENTION_ANALYTICS.CREATE_SELECT, {
            mention_type: clean.type,
            mention_name: clean.name,
            mention_current_user: clean.id === currentUserId,
            selected_from_recents: selectedFromRecents,
          });
        }}
        popoverContainer={renderPopover}
        entryComponent={({
          mention: rawMention,
          isFocused,
          searchValue,
          onMouseDown,
          onMouseUp,
        }) => {
          const mention = rawMention as MentionData;

          return (
            <>
              {isLoading && isFirst(suggestions, mention) && (
                <LoadingSuggestion searchValue={searchValue} />
              )}
              {isFirstRecentsSuggestion(suggestions, mention) && (
                <MenuCategory
                  title={t('RECENT', { count: recentsCount })}
                  style={categoryStyle}
                />
              )}
              {!mention.fromRecents &&
                canShowCategory(nonRecentsSuggestions, mention) && (
                  <MenuCategory
                    title={t(mention.type.toUpperCase())}
                    style={categoryStyle}
                  />
                )}
              {renderSuggestion({
                mention,
                isFocused,
                searchValue,
                onMouseDown,
                onMouseUp,
              })}
            </>
          );
        }}
      />
    </StyledDiv>
  );
};

MentionPlugin.displayName = 'MentionPlugin';

const editorStateFromRaw = (raw: RawDraftContentState) =>
  EditorState.createWithContent(convertFromRaw(raw));

export default Object.assign(WithAlloySuspense(MentionPlugin), {
  PLACEMENTS: Overlay.PLACEMENTS,
  EMPTY_STATE: EditorState.createEmpty(),
  editorStateFromRaw,
  TYPES: MENTION_TYPES,
});
